import React from 'react';
import i18n from '../i18n';

/**
 * Last-resort safety net. Without this, ANY uncaught render error anywhere
 * in the tree — a null-pointer TypeError in one card, one modal, one
 * screen — unmounts the whole app to a blank/black screen with no way
 * back except force-quitting and relaunching. That's the "random black
 * screen" bug users have reported: it's not one specific action, it's
 * whichever component happens to throw next.
 *
 * This boundary catches it, reports the error (PostHog if available,
 * console always) so future occurrences come with an actual stack trace,
 * and auto-recovers by navigating to the dashboard — no button, no
 * decision for the user to make while staring at a broken screen.
 *
 * Loop guard: if this fires repeatedly in a short window (the same bug
 * re-triggering on every reload), auto-reloading forever would be worse
 * than the blank screen it replaces. After 3 catches within 30s we stop
 * auto-navigating and show a manual button instead.
 */
const LOOP_KEY = 'tofbox_error_boundary_hits';
const LOOP_WINDOW_MS = 30_000;
const LOOP_LIMIT = 3;

function recordHitAndCheckLoop() {
  let hits = [];
  try { hits = JSON.parse(sessionStorage.getItem(LOOP_KEY) || '[]'); } catch { /* noop */ }
  const now = Date.now();
  hits = hits.filter((t) => now - t < LOOP_WINDOW_MS);
  hits.push(now);
  try { sessionStorage.setItem(LOOP_KEY, JSON.stringify(hits)); } catch { /* noop */ }
  return hits.length >= LOOP_LIMIT;
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, looping: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] caught render error:', error, info?.componentStack);
    try {
      window.posthog?.capture?.('app_render_error', {
        message: error?.message,
        stack: error?.stack,
        componentStack: info?.componentStack,
      });
    } catch { /* never let telemetry itself crash the fallback */ }

    const looping = recordHitAndCheckLoop();
    if (looping) {
      this.setState({ looping: true });
    } else {
      // Auto-recover silently — no button to tap, no message to read.
      window.location.href = '/dashboard';
    }
  }

  handleReload = () => {
    try { sessionStorage.removeItem(LOOP_KEY); } catch { /* noop */ }
    window.location.href = '/dashboard';
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    // Mid-auto-recovery (not looping): render nothing rather than a flash
    // of fallback UI right before the navigation kicks in.
    if (!this.state.looping) return null;
    return (
      <div
        data-testid="app-error-boundary"
        className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center text-center px-6"
      >
        <img src="/logo-negatif.png" alt="TofBox" className="w-16 h-16 object-contain mb-5 opacity-70" />
        <h1 className="text-white text-lg font-black font-outfit mb-2">{i18n.t('errorBoundary.title')}</h1>
        <p className="text-white/50 text-sm mb-6 max-w-xs">
          {i18n.t('errorBoundary.body')}
        </p>
        <button
          onClick={this.handleReload}
          data-testid="app-error-reload-btn"
          className="px-6 py-3 bg-[#534AB7] text-white text-sm font-bold uppercase tracking-wider rounded-xl active:scale-95 transition-transform"
        >
          {i18n.t('errorBoundary.reload')}
        </button>
      </div>
    );
  }
}
